<template>
  <div class="login-modal-overlay" @click="$emit('close')">
    <div class="login-modal-content" @click.stop :class="{ 'dark-mode': themeStore.isDark }">
      <button class="btn-close-modal" @click="$emit('close')" aria-label="Fechar">
        <i class="uil uil-times"></i>
      </button>

      <!-- TELA 1: LOGIN -->
      <div v-if="mode === 'login'">
        <div class="text-center mb-4">
          <h3 class="fw-bold mb-2 modal-title-text font-cinzel">Acessar Conta</h3>
          <p class="modal-subtitle-text small">Faça login para acessar o ecossistema Bar Experts.</p>
        </div>
        
        <form @submit.prevent="handleLogin">
          <div v-if="error" class="alert alert-danger py-2 small">
            {{ error }}
          </div>

          <div class="mb-3 text-start">
            <label class="form-label fw-semibold small">E-mail ou Usuário</label>
            <input v-model="email" type="text" class="form-control form-control-lg custom-input" required placeholder="Ex: usuario@barexperts.com.br" />
          </div>
          
          <div class="mb-3 text-start">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="form-label fw-semibold small mb-0">Senha</label>
              <a href="#" @click.prevent="switchToReset" class="small modal-link-text text-decoration-none">Esqueceu a senha?</a>
            </div>
            <input v-model="password" type="password" class="form-control form-control-lg custom-input" required />
          </div>

          <button type="submit" class="btn btn-theme btn-lg w-100 mb-2 mt-2" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ loading ? 'Entrando...' : 'Entrar' }}
          </button>

          <router-link 
            v-if="authStore.isAuthenticated" 
            to="/dashboard" 
            @click="$emit('close')" 
            class="btn btn-outline-danger btn-sm w-100 fw-bold"
          >
            <i class="bi-grid-fill me-1"></i> Ir para o Painel / Dashboard
          </router-link>
        </form>

        <div class="mt-4 text-center">
          <p class="small modal-subtitle-text mb-0">
            Ainda não tem acesso? <router-link to="/planos" @click="$emit('close')" class="fw-bold text-decoration-none modal-link-text">Ver Planos</router-link>
          </p>
        </div>
      </div>

      <!-- TELA 2: RECUPERAÇÃO - SOLICITAR CHAVE -->
      <div v-else-if="mode === 'forgot_request'">
        <div class="text-center mb-4">
          <h3 class="fw-bold mb-2 modal-title-text">Recuperar Senha</h3>
          <p class="modal-subtitle-text small">Enviaremos uma chave de segurança confidencial para o seu e-mail cadastrado.</p>
        </div>

        <form @submit.prevent="handleRequestReset">
          <div v-if="error" class="alert alert-danger py-2 small">
            {{ error }}
          </div>

          <div class="mb-4 text-start">
            <label class="form-label fw-semibold small">Seu E-mail ou Usuário</label>
            <input v-model="resetEmail" type="text" class="form-control form-control-lg custom-input" required placeholder="Ex: usuario@barexperts.com.br" />
          </div>

          <button type="submit" class="btn btn-theme btn-lg w-100 mb-2" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ loading ? 'Gerando Chave de Segurança...' : 'Enviar Chave de Segurança' }}
          </button>

          <button type="button" class="btn btn-outline-secondary btn-sm w-100" @click="mode = 'login'" :disabled="loading">
            Voltar ao Login
          </button>
        </form>
      </div>

      <!-- TELA 3: RECUPERAÇÃO - DIGITAR CHAVE E NOVA SENHA -->
      <div v-else-if="mode === 'forgot_verify'">
        <div class="text-center mb-3">
          <h3 class="fw-bold mb-1 modal-title-text">Confirmar Chave de Segurança</h3>
          <p class="modal-subtitle-text small">
            Verifique a chave de segurança enviada para <strong>{{ targetEmailDisplay }}</strong>.
          </p>
        </div>

        <form @submit.prevent="handleVerifyReset">
          <div v-if="error" class="alert alert-danger py-2 small">
            {{ error }}
          </div>

          <div class="mb-3 text-start">
            <label class="form-label fw-semibold small text-danger">Chave de Segurança Recebida</label>
            <input v-model="cocktailName" type="text" class="form-control form-control-lg custom-input text-uppercase fw-bold" required placeholder="Digite o código recebido no e-mail" />
          </div>

          <div class="mb-3 text-start">
            <label class="form-label fw-semibold small">Nova Senha</label>
            <input v-model="newPassword" type="password" class="form-control custom-input" minlength="6" required placeholder="Mínimo 6 caracteres" />
          </div>

          <div class="mb-4 text-start">
            <label class="form-label fw-semibold small">Confirmar Nova Senha</label>
            <input v-model="confirmPassword" type="password" class="form-control custom-input" minlength="6" required />
          </div>

          <button type="submit" class="btn btn-theme btn-lg w-100 mb-2" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ loading ? 'Redefinindo...' : 'Redefinir Senha' }}
          </button>

          <div class="d-flex justify-content-between mt-2">
            <button type="button" class="btn btn-link btn-sm text-decoration-none modal-subtitle-text p-0" @click="mode = 'forgot_request'">
              Reenviar
            </button>
            <button type="button" class="btn btn-link btn-sm text-decoration-none modal-link-text p-0" @click="mode = 'login'">
              Voltar ao Login
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useNotificationStore } from '@/stores/notificationStore'
import { requestPasswordResetApi, verifyPasswordResetApi } from '@/services/api'

const emit = defineEmits(['close', 'success'])

const mode = ref('login') // 'login' | 'forgot_request' | 'forgot_verify'
const email = ref('')
const password = ref('')
const resetEmail = ref('')
const targetEmailDisplay = ref('')
const verifiedTargetEmail = ref('')
const cocktailName = ref('')
const newPassword = ref('')
const confirmPassword = ref('')

const error = ref('')
const loading = ref(false)

const authStore = useAuthStore()
const themeStore = useThemeStore()
const notificationStore = useNotificationStore()

function switchToReset() {
  resetEmail.value = email.value
  error.value = ''
  mode.value = 'forgot_request'
}

async function handleLogin() {
  error.value = ''
  loading.value = true
  try {
    await authStore.login(email.value, password.value)
    notificationStore.notifySuccess('Login realizado com sucesso! Bem-vindo(a) de volta.')
    emit('success')
    emit('close')
  } catch (err) {
    error.value = err.response?.data?.error || 'Erro ao realizar login. Verifique suas credenciais.'
    notificationStore.notifyError('Falha na autenticação.')
  } finally {
    loading.value = false
  }
}

async function handleRequestReset() {
  error.value = ''
  loading.value = true
  try {
    const res = await requestPasswordResetApi(resetEmail.value)
    targetEmailDisplay.value = res.data?.masked_email || res.data?.email || resetEmail.value
    verifiedTargetEmail.value = res.data?.email || resetEmail.value
    notificationStore.notifySuccess(res.data?.message || 'Se a conta existir, enviamos um código para o e-mail cadastrado.')
    mode.value = 'forgot_verify'
  } catch (err) {
    error.value = err.response?.data?.error || 'Erro ao solicitar recuperação de senha.'
    notificationStore.notifyError(error.value)
  } finally {
    loading.value = false
  }
}

async function handleVerifyReset() {
  error.value = ''
  if (newPassword.value !== confirmPassword.value) {
    error.value = 'As senhas digitadas não coincidem.'
    notificationStore.notifyWarning('As senhas não coincidem.')
    return
  }

  loading.value = true
  try {
    const res = await verifyPasswordResetApi(verifiedTargetEmail.value, cocktailName.value, newPassword.value)
    notificationStore.notifySuccess(res.data?.message || 'Senha alterada com sucesso! Faça seu login.')
    email.value = verifiedTargetEmail.value
    password.value = ''
    cocktailName.value = ''
    newPassword.value = ''
    confirmPassword.value = ''
    mode.value = 'login'
  } catch (err) {
    error.value = err.response?.data?.error || 'Erro ao validar chave ou redefinir senha.'
    notificationStore.notifyError(error.value)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

.login-modal-content {
  position: relative;
  width: 100%;
  max-width: 400px;
  background-color: #fcfbf8; /* Creme suave */
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  border: 1px solid rgba(0,0,0,0.05);
  animation: slideUpFade 0.3s ease;
  color: #333;
}

.modal-subtitle-text {
  color: #666;
}

.modal-link-text {
  color: var(--be-accent-text);
}

.modal-link-text:hover {
  filter: brightness(0.8);
}

/* Modo Escuro */
.login-modal-content.dark-mode {
  background-color: rgba(24, 24, 24, 0.85); /* Cinza escuro translúcido (#181818) */
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  color: #f8f9fa;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 40px rgba(0,0,0,0.5);
}

.login-modal-content.dark-mode .modal-subtitle-text {
  color: #adb5bd;
}

/* Inputs Customizados para se adaptarem ao tema */
.custom-input {
  background-color: #fff;
  border: 1px solid #ddd;
  color: #333;
  transition: all 0.2s;
}

.custom-input:focus {
  border-color: var(--cnvs-themecolor);
  box-shadow: 0 0 0 0.25rem rgba(var(--cnvs-themecolor-rgb), 0.25);
}

.dark-mode .custom-input {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
}

.dark-mode .custom-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.dark-mode .custom-input:focus {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: var(--cnvs-themecolor);
  color: #fff;
  box-shadow: 0 0 0 0.25rem rgba(var(--cnvs-themecolor-rgb), 0.35);
}

/* Botão com a cor do tema e não azul do bootstrap */
.btn-theme {
  background-color: var(--cnvs-themecolor);
  border-color: var(--cnvs-themecolor);
  color: #fff;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-theme:hover, .btn-theme:focus {
  background-color: var(--cnvs-themecolor);
  border-color: var(--cnvs-themecolor);
  filter: brightness(0.85);
  color: #fff;
}

.btn-close-modal {
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: #888;
  cursor: pointer;
  transition: color 0.2s;
  line-height: 1;
}

.btn-close-modal:hover {
  color: #333;
}

.dark-mode .btn-close-modal:hover {
  color: #fff;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUpFade {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
